import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { captureMessage } from '@sentry/browser';
import { isEmpty, isNil } from 'ramda';
import { useHttpCalls } from '../api/useHttpCalls';
import type { Preset, PutPresetRequest, QueryIdRequestBody } from '../types/Api';
import type { QueryFilterObject } from '../types/QueryFilterObject';
import { objectHasEveryEqualPropOfAnother } from '../utils/objectHasEveryEqualPropOfAnother';
type FilterModel = QueryIdRequestBody['filters'];
type UseLoadPresetsProps = {
  gridCode?: string;
  presetInstanceId?: string;
  onApiError?: (error: FetchBaseQueryError) => void;
  queryModifier?: (filter: QueryFilterObject) => QueryFilterObject;
};
export const useLoadPresets = (props: UseLoadPresetsProps) => {
  const http = useHttpCalls({
    gridCode: props.gridCode,
    presetInstanceId: props.presetInstanceId,
    onApiError: props?.onApiError
  });
  const handleUpdatePresetForCurrentQueryModifier = async (preset: Preset) => {
    // No queryModifier → no static filters to reconcile → skip entirely
    if (!props.queryModifier) {
      return;
    }
    const data = await http.getDataQuery(preset.dataQueryId);
    if (isNil(data) || isNil(data?.filters)) {
      return;
    }
    const staticFilters = props.queryModifier ? props.queryModifier({}) : {};
    const isAllStaticFilterPresent = objectHasEveryEqualPropOfAnother(staticFilters, data.filters);
    if (isAllStaticFilterPresent) {
      return;
    }
    const filters = {
      ...data.filters,
      ...staticFilters
    } as FilterModel;
    await http.createDataQuery({
      filters,
      order: data.order,
      smartSearch: data.smartSearch ?? ''
    }).then(async queryIdResponseBody => {
      if (!queryIdResponseBody) {
        return;
      }
      const newDataQueryId = queryIdResponseBody.dataQueryId;
      const presetData = await http.getPreset(preset.id);
      if (isNil(presetData) || isNil(presetData?.gridSettings) || isEmpty(presetData?.gridSettings) || isNil(presetData?.columnsSettings) || isEmpty(presetData?.columnsSettings)) {
        captureMessage('updatePreset', scope => scope.setLevel('error').setExtras({
          userDescription: 'update custom preset from useLoadPresets',
          state: presetData
        }));
        return;
      }
      const modifiedPreset: PutPresetRequest = {
        gridSettings: presetData.gridSettings,
        columnsSettings: presetData.columnsSettings,
        dataQueryId: newDataQueryId
      };
      http.updatePreset(preset.id, modifiedPreset)?.catch(() => {
        // Silent catch — queryModifier reconciliation is best-effort.
      });
    });
  };
  const loadPresets = () => http.getPresets().then(async response => {
    if (!response) {
      return;
    }
    await Promise.all([...response.map(preset => handleUpdatePresetForCurrentQueryModifier(preset))]);
    return response;
  });
  return loadPresets;
};